/**
 * Historial — MOBILE: lista de sesiones y, al tocar una, sus series con las correcciones a un
 * toque. La corrección se hace en hoja inferior porque muchas veces se corrige ahí mismo en el
 * gimnasio, al darse cuenta del error.
 *
 * La lista dice que es editable y cada tarjeta lleva su chevron. Sin eso, las tarjetas se veían
 * exactamente igual que las de Plan, que no se tocan: la pantalla que responde "¿cómo lo corrijo?"
 * no puede esconder que se corrige tocando.
 */

import { useState, type ReactNode } from 'react';
import { fechaLarga, formatearDuracion, formatearNumero, type Serie } from '@mg/core';

import { useHistorial } from '../features/useHistorial.ts';
import { HojaInferior } from '../ui/Shell.mobile.tsx';
import { Aviso, Campo, Icono, Insignia, Selector, Stepper, Vacio } from '../ui/componentes.tsx';

export function HistorialMobile(): ReactNode {
  const h = useHistorial();
  const [corrigiendo, setCorrigiendo] = useState<Serie | null>(null);
  const [moviendo, setMoviendo] = useState<string | null>(null);

  if (h.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (h.sesiones.length === 0 && h.arrastradas.length === 0) {
    return <Vacio>Todavía no hay sesiones registradas.</Vacio>;
  }

  const detalle = h.detalle;

  if (detalle) {
    return (
      <>
        <button type="button" className="boton boton--fantasma boton--chico" onClick={() => h.setAbierta(null)}>
          ← Todas las sesiones
        </button>

        <section className="tarjeta">
          <span className="eyebrow">{detalle.sesion.estado}</span>
          <h2 className="titulo" style={{ marginTop: 2 }}>
            {fechaLarga(detalle.sesion.fecha)}
          </h2>
          <p className="nota">{detalle.nombreDia ?? 'Sin día de rutina'}</p>

          <div className="fila fila--entre" style={{ marginTop: 'var(--e3)' }}>
            <span className="nota--fina">
              <span className="lectura">{detalle.seriesHechas}</span> series
            </span>
            <span className="nota--fina">
              <span className="lectura">{formatearNumero(detalle.volumenKg / 1000, 2)} t</span> de volumen
            </span>
          </div>

          {detalle.sesion.notas && (
            <p className="nota" style={{ marginTop: 'var(--e2)' }}>
              {detalle.sesion.notas}
            </p>
          )}

          <button
            type="button"
            className="boton boton--chico boton--fantasma"
            style={{ marginTop: 'var(--e3)' }}
            onClick={() => setMoviendo(detalle.sesion.id)}
          >
            Mover a otra fecha
          </button>
        </section>

        {detalle.ejercicios.map((e) => (
          <section key={e.ejercicio.id} className="tarjeta">
            <div className="fila fila--entre">
              <strong>{e.ejercicio.nombre}</strong>
              {e.omitida && <Insignia tono="alerta">{h.nombreMotivo(e.omitida.motivo_omision)}</Insignia>}
            </div>

            <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
              {e.completadas.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="boton boton--fantasma"
                  style={{ justifyContent: 'space-between', minHeight: 48 }}
                  onClick={() => setCorrigiendo(s)}
                >
                  <span className="nota--fina">serie {s.numero_serie}</span>
                  <span className="lectura">
                    {formatearNumero(s.peso)} {s.unidad} × {s.reps}
                    {s.rir !== null && <span className="apagado"> · RIR {s.rir}</span>}
                  </span>
                </button>
              ))}
              {e.completadas.length === 0 && <p className="nota--fina">Sin series completadas.</p>}
            </div>
          </section>
        ))}

        {detalle.cardio && (
          <section className="tarjeta">
            <strong>Cardio</strong>
            <p className="lectura" style={{ marginTop: 4 }}>
              {formatearDuracion(detalle.cardio.duracion_seg)} · {formatearNumero(detalle.cardio.distancia_km, 2)} km ·{' '}
              {formatearNumero(detalle.cardio.calorias, 0)} cal
            </p>
            {detalle.cardio.nivel_resistencia === null ? (
              <p className="nota--fina" style={{ color: 'var(--ambar)', marginTop: 4 }}>
                Sin nivel de resistencia: no comparable con otras sesiones.
              </p>
            ) : (
              <p className="nota--fina" style={{ marginTop: 4 }}>
                Nivel {detalle.cardio.nivel_resistencia}
              </p>
            )}
          </section>
        )}

        {corrigiendo && (
          <HojaInferior titulo="Corregir la serie" onCerrar={() => setCorrigiendo(null)}>
            <HojaCorreccion h={h} serie={corrigiendo} onListo={() => setCorrigiendo(null)} />
          </HojaInferior>
        )}

        {moviendo && (
          <HojaInferior
            titulo="Mover la sesión"
            onCerrar={() => {
              h.limpiarMovida();
              setMoviendo(null);
            }}
          >
            <HojaMover
              h={h}
              sesionId={moviendo}
              onListo={() => {
                h.limpiarMovida();
                setMoviendo(null);
              }}
            />
          </HojaInferior>
        )}
      </>
    );
  }

  return (
    <>
      <p className="nota">Tocá una sesión para corregir peso, reps, el ejercicio o la fecha.</p>

      {h.sesiones.map((s) => (
        <button
          key={s.sesion.id}
          type="button"
          className="tarjeta"
          onClick={() => h.setAbierta(s.sesion.id)}
          style={{ textAlign: 'left', color: 'inherit', cursor: 'pointer' }}
        >
          <div className="fila fila--entre">
            <div>
              <div className="fila" style={{ gap: 6 }}>
                <span className="lectura" style={{ fontSize: 15 }}>
                  {s.sesion.fecha}
                </span>
                {s.sesion.estado === 'omitida' && <Insignia tono="alerta">omitida</Insignia>}
                {s.sesion.estado === 'en_curso' && <Insignia tono="ambar">en curso</Insignia>}
              </div>
              <p className="nota--fina" style={{ marginTop: 2 }}>
                {s.nombreDia ?? 'Sin día de rutina'}
              </p>
            </div>

            <div className="fila" style={{ gap: 'var(--e3)' }}>
              <div style={{ textAlign: 'right' }}>
                <span className="lectura">{s.seriesHechas} series</span>
                <p className="nota--fina">
                  <span className="lectura">{formatearNumero(s.volumenKg / 1000, 2)} t</span> de volumen
                </p>
              </div>
              <span
                className="apagado"
                aria-hidden="true"
                style={{ display: 'flex', flex: 'none', transform: 'rotate(180deg)' }}
              >
                <Icono nombre="atras" tamano={18} />
              </span>
            </div>
          </div>
        </button>
      ))}

      {/* Lo arrastrado no es historial —todavía no pasó— pero es la consecuencia de un omitido y
          desaparecía de la vista al sacar las sesiones planificadas de la lista. */}
      {h.arrastradas.length > 0 && (
        <section className="tarjeta">
          <span className="eyebrow">Todavía pendiente</span>
          <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
            {h.arrastradas.map((a) => (
              <div key={`${a.ejercicio.id}-${a.hacia}`}>
                <div className="fila" style={{ gap: 6 }}>
                  <strong style={{ fontSize: 14 }}>{a.ejercicio.nombre}</strong>
                  <Insignia tono="ambar">arrastrada</Insignia>
                </div>
                <p className="nota--fina" style={{ marginTop: 2 }}>
                  Viene de la sesión anterior{a.desde ? ` (${fechaLarga(a.desde)})` : ''}:{' '}
                  <span className="lectura">{a.series}</span> series que te esperan el {fechaLarga(a.hacia)}.
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function HojaCorreccion({
  h,
  serie,
  onListo,
}: {
  h: ReturnType<typeof useHistorial>;
  serie: Serie;
  onListo: () => void;
}): ReactNode {
  const [peso, setPeso] = useState(serie.peso);
  const [reps, setReps] = useState(serie.reps);
  const [nuevoEjercicio, setNuevoEjercicio] = useState(serie.ejercicio_id);
  const maquina = h.datos.maquinaDe(serie.ejercicio_id);
  const incremento = maquina?.incremento_placa ?? (serie.unidad === 'lb' ? 5 : 2.5);

  return (
    <>
      <Stepper etiqueta="Peso" valor={peso} incremento={incremento} onCambio={setPeso} unidad={serie.unidad} />
      <Stepper etiqueta="Reps" valor={reps} incremento={1} min={1} onCambio={setReps} />

      <button
        type="button"
        className="boton boton--principal"
        onClick={async () => {
          await h.editar(serie, { peso, reps });
          onListo();
        }}
      >
        Guardar el cambio
      </button>

      <hr className="separador" />

      <Selector
        etiqueta="Estaba en el ejercicio equivocado"
        value={nuevoEjercicio}
        onChange={(e) => setNuevoEjercicio(e.target.value)}
      >
        {h.datos.ejercicios.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nombre}
          </option>
        ))}
      </Selector>

      <button
        type="button"
        className="boton"
        disabled={nuevoEjercicio === serie.ejercicio_id}
        onClick={async () => {
          await h.reasignar(serie, nuevoEjercicio);
          onListo();
        }}
      >
        Reasignar sin borrar el registro
      </button>

      <p className="nota--fina">
        {nuevoEjercicio === serie.ejercicio_id
          ? 'Elegí otro ejercicio para poder reasignarla.'
          : 'Reasignar conserva la fila: el peso que anotaste era correcto, sólo estaba en el ejercicio equivocado.'}
      </p>

      {h.error && <Aviso tono="importante">{h.error}</Aviso>}
    </>
  );
}

function HojaMover({
  h,
  sesionId,
  onListo,
}: {
  h: ReturnType<typeof useHistorial>;
  sesionId: string;
  onListo: () => void;
}): ReactNode {
  const sesion = h.datos.sesiones.find((s) => s.id === sesionId)!;
  const [fecha, setFecha] = useState(sesion.fecha);
  const movida = h.movida?.sesionId === sesionId ? h.movida : null;

  // Mover y cerrar la hoja dejaba la promesa de acá abajo sin cumplir: §4.2 pide que el recálculo
  // se explique, así que la hoja se queda mostrando qué cambió hasta que el usuario lo lee.
  if (movida) {
    return (
      <>
        <Aviso tono="info" titulo="Qué cambió">
          <span style={{ display: 'block' }}>La sesión quedó en el {fechaLarga(movida.fecha)}.</span>
          {movida.cambios.length === 0 ? (
            <span style={{ display: 'block' }}>En el plan no cambió nada.</span>
          ) : (
            movida.cambios.map((x) => (
              <span key={x.texto} style={{ display: 'block' }}>
                {x.texto}
              </span>
            ))
          )}
        </Aviso>
        <button type="button" className="boton boton--principal" onClick={onListo}>
          Entendido
        </button>
      </>
    );
  }

  return (
    <>
      <Campo etiqueta="Nueva fecha" texto type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      <p className="nota">
        Las series se mueven con la sesión. Si la sesión es parte del plan, el calendario se
        recalcula y te muestra qué cambió.
      </p>
      <button
        type="button"
        className="boton boton--principal boton--registrar"
        disabled={fecha === sesion.fecha}
        onClick={() => void h.mover(sesion, fecha)}
      >
        Mover al {fecha}
      </button>
      {fecha === sesion.fecha && <p className="nota--fina">Elegí una fecha distinta a la de ahora.</p>}
      {h.error && <Aviso tono="importante">{h.error}</Aviso>}
    </>
  );
}
